import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, getDoc, getDocFromServer, getDocs, updateDoc, onSnapshot, query, where, orderBy, limit, increment, serverTimestamp, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Firebase configuration
// TODO: Replace with your Firebase project configuration
const firebaseConfig = {
  apiKey: "AIzaSyBdtV4fgzz90p1I2aBj9Wmyv2QRXvNLrLs",
  authDomain: "charade-3bbf6.firebaseapp.com",
  projectId: "charade-3bbf6",
  storageBucket: "charade-3bbf6.firebasestorage.app",
  messagingSenderId: "826056337221",
  appId: "1:826056337221:web:a45397559641056ac1402c"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

// Export Firestore functions for direct use (needed for screen wake re-sync)
export { doc, getDoc, getDocFromServer, runTransaction, increment } from 'firebase/firestore';

// Auth providers
export const googleProvider = new GoogleAuthProvider();

// Auth functions
export const signInWithGoogle = async () => {
  try {
    // Use popup for Google sign-in (works better with custom domains)
    const result = await signInWithPopup(auth, googleProvider);

    // Create or update user profile
    await createOrUpdateUserProfile(result.user);

    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    // Provide user-friendly error messages
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    }
    throw error;
  }
};

// Handle redirect result after Google sign-in
export const handleGoogleRedirectResult = async () => {
  try {
    console.log('🔄 Checking for Google redirect result...');
    console.log('Auth instance:', auth);
    console.log('Current user before getRedirectResult:', auth.currentUser);

    const result = await getRedirectResult(auth);
    console.log('getRedirectResult returned:', result);

    if (result?.user) {
      console.log('✅ Google redirect result found!');
      console.log('User details:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });
      console.log('📝 Creating/updating user profile...');

      // Create or update user profile
      await createOrUpdateUserProfile(result.user);
      console.log('✅ User profile created/updated successfully');
      return result.user;
    }

    console.log('ℹ️ No redirect result (user did not just sign in via Google)');
    console.log('Current user after getRedirectResult:', auth.currentUser);
    return null;
  } catch (error) {
    console.error('❌ Error handling redirect result:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', error);
    throw error;
  }
};

export const signUpWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    // Create user profile
    await createOrUpdateUserProfile(user, displayName);

    return user;
  } catch (error) {
    console.error('Error signing up with email:', error);
    throw error;
  }
};

export const signInWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in with email:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// User profile functions
export const createOrUpdateUserProfile = async (user, displayName = null) => {
  const userRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    // Create new user profile
    await setDoc(userRef, {
      uid: user.uid,
      email: user.email,
      displayName: displayName || user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      createdAt: serverTimestamp(),
      level: 1,
      xp: 0,
      gamesPlayed: 0,
      gamesWon: 0,
      totalPoints: 0,
      achievements: [],
      isPremium: false,
      freeGamesRemaining: 3, // 3 total games lifetime for free users
      subscriptionStatus: 'free',
      subscriptionEndDate: null,
      referralCode: generateReferralCode(),
      referredBy: null,
      customWordPacks: []
    });
  } else {
    // Update existing user
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp()
    });
  }
};

export const getUserProfile = async (uid) => {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);
  return userDoc.exists() ? userDoc.data() : null;
};

export const updateUserProfile = async (uid, updates) => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, updates);
};

// Game functions
export const createGame = async (hostId, settings) => {
  const gameId = generateGameId();
  const gameRef = doc(db, 'games', gameId);

  await setDoc(gameRef, {
    gameId,
    hostId,
    status: 'lobby',
    settings: {
      timer: settings.timer || 60,
      wordPacks: settings.wordPacks || ['classic'],
      allowCustomWords: settings.allowCustomWords !== false,
      rounds: settings.rounds || 'unlimited'
    },
    // Team selection mode: 'random' or 'teamSelection'
    teamSelectionMode: settings.teamSelectionMode || 'random',
    players: {},
    teams: { teamA: [], teamB: [] },
    // Track player team confirmations (for teamSelection mode)
    playerTeamConfirmations: {},
    // Players waiting for next round (joined mid-game or didn't confirm)
    waitingPlayers: {},
    currentRound: 0,
    scores: { teamA: 0, teamB: 0 },
    playerPoints: {}, // Track individual player points
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return gameId;
};

export const joinGame = async (gameId, userId, playerName) => {
  const gameRef = doc(db, 'games', gameId);
  // CRITICAL: Use getDocFromServer to bypass cache and get fresh data
  // This ensures we correctly detect if game is 'playing' for mid-game joins
  const gameDoc = await getDocFromServer(gameRef);

  if (!gameDoc.exists()) {
    throw new Error('Game not found');
  }

  const game = gameDoc.data();

  const updates = {
    updatedAt: serverTimestamp()
  };

  // If game is already in progress (playing status), add to waiting players
  // Note: We only check status === 'playing', not teamsAssigned, to avoid race condition
  // where status changes to 'playing' before the cloud function sets teamsAssigned
  if (game.status === 'playing') {
    console.log(`⏳ Player ${playerName} joining mid-game - adding to waiting list`);

    updates[`waitingPlayers.${userId}`] = {
      userId,
      name: playerName,
      joinedAt: serverTimestamp(),
      team: null,
      isActive: true
    };
  } else {
    // Game is in lobby - add to players directly
    updates[`players.${userId}`] = {
      userId,
      name: playerName,
      joinedAt: serverTimestamp(),
      team: null,
      isActive: true
    };
  }

  await updateDoc(gameRef, updates);

  return gameDoc.data();
};

/**
 * Select a team (for teamSelection mode)
 * Players can change teams even after confirming - this will clear their confirmation
 */
export const selectTeam = async (gameId, userId, team) => {
  const gameRef = doc(db, 'games', gameId);
  // CRITICAL: Use getDocFromServer to bypass cache and get fresh data
  // This ensures we see the player in their correct location after server-side transactions
  const gameDoc = await getDocFromServer(gameRef);

  if (!gameDoc.exists()) {
    throw new Error('Game not found');
  }

  const game = gameDoc.data();

  // Check if player is in players or waitingPlayers
  const isInPlayers = game.players?.[userId];
  const isInWaiting = game.waitingPlayers?.[userId];

  if (!isInPlayers && !isInWaiting) {
    throw new Error('Player not found in game');
  }

  const updates = {
    updatedAt: serverTimestamp()
  };

  // Update team based on where player is
  if (isInPlayers) {
    updates[`players.${userId}.team`] = team;
  } else {
    updates[`waitingPlayers.${userId}.team`] = team;
  }

  // Clear confirmation when team changes (player needs to re-confirm)
  // This allows players to switch teams even after confirming
  updates[`playerTeamConfirmations.${userId}`] = false;

  await updateDoc(gameRef, updates);
};

/**
 * Confirm team selection
 */
export const confirmTeamSelection = async (gameId, userId) => {
  const gameRef = doc(db, 'games', gameId);
  // CRITICAL: Use getDocFromServer to bypass cache and get fresh data
  // This ensures we see the player in their correct location after server-side transactions
  const gameDoc = await getDocFromServer(gameRef);

  if (!gameDoc.exists()) {
    throw new Error('Game not found');
  }

  const game = gameDoc.data();

  // Check if player has a team selected
  const player = game.players?.[userId] || game.waitingPlayers?.[userId];
  if (!player) {
    throw new Error('Player not found in game');
  }

  if (!player.team) {
    throw new Error('Must select a team before confirming');
  }

  await updateDoc(gameRef, {
    [`playerTeamConfirmations.${userId}`]: true,
    updatedAt: serverTimestamp()
  });
};

/**
 * Update team selection mode (host only, before game starts)
 */
export const setTeamSelectionMode = async (gameId, mode) => {
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    teamSelectionMode: mode,
    updatedAt: serverTimestamp()
  });
};

/**
 * Reset team confirmations (used between rounds in teamSelection mode)
 */
export const resetTeamConfirmations = async (gameId) => {
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    playerTeamConfirmations: {},
    updatedAt: serverTimestamp()
  });
};

export const subscribeToGame = (gameId, callback) => {
  const gameRef = doc(db, 'games', gameId);
  return onSnapshot(gameRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data());
    } else {
      // Game doesn't exist - notify callback with null
      callback(null);
    }
  });
};

export const updateGame = async (gameId, updates) => {
  const gameRef = doc(db, 'games', gameId);
  await updateDoc(gameRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

// Word pack functions
export const getWordPacks = async () => {
  const packsRef = collection(db, 'wordPacks');
  const snapshot = await getDocs(packsRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Leaderboard functions
export const getGlobalLeaderboard = async (limitCount = 100) => {
  const usersRef = collection(db, 'users');
  const q = query(
    usersRef,
    orderBy('totalPoints', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc, index) => ({
    rank: index + 1,
    ...doc.data()
  }));
};

// Achievement functions
export const unlockAchievement = async (userId, achievementId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const achievements = userDoc.data().achievements || [];

    if (!achievements.includes(achievementId)) {
      achievements.push(achievementId);

      await updateDoc(userRef, {
        achievements,
        updatedAt: serverTimestamp()
      });

      return true; // Achievement unlocked
    }
  }

  return false; // Achievement already unlocked
};

// Subscription functions
export const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
export const verifySubscription = httpsCallable(functions, 'verifySubscription');

// Game round management functions
export const startGame = httpsCallable(functions, 'startGame');
export const startNextRound = httpsCallable(functions, 'startNextRound');

// Utility functions
function generateGameId() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function generateReferralCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Free games tracking (3 total games lifetime for free users, no monthly reset)
export const checkFreeGamesRemaining = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const userData = userDoc.data();

    // Check if user is whitelisted for free testing
    const isWhitelisted = await isUserWhitelisted(userData.email);
    if (isWhitelisted) {
      return 999; // Unlimited games for whitelisted testers
    }

    // Return remaining free games (no monthly reset - 3 total lifetime)
    return userData.freeGamesRemaining ?? 3;
  }

  return 0;
};

export const decrementFreeGames = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const userData = userDoc.data();

    // Don't decrement for whitelisted users
    const isWhitelisted = await isUserWhitelisted(userData.email);
    if (isWhitelisted) {
      return; // Skip decrement for whitelisted testers
    }
  }

  await updateDoc(userRef, {
    freeGamesRemaining: increment(-1)
  });
};

// Whitelist management for testing users
export const isUserWhitelisted = async (email) => {
  if (!email) return false;

  try {
    const whitelistRef = doc(db, 'whitelist', email.toLowerCase());
    const whitelistDoc = await getDoc(whitelistRef);
    return whitelistDoc.exists() && whitelistDoc.data()?.active === true;
  } catch (error) {
    console.error('Error checking whitelist:', error);
    return false;
  }
};

export const addUserToWhitelist = async (email, notes = '') => {
  try {
    const whitelistRef = doc(db, 'whitelist', email.toLowerCase());
    await setDoc(whitelistRef, {
      email: email.toLowerCase(),
      active: true,
      notes: notes,
      addedAt: serverTimestamp()
    });
    console.log(`✅ Added ${email} to whitelist`);
    return true;
  } catch (error) {
    console.error('Error adding to whitelist:', error);
    return false;
  }
};

export const removeUserFromWhitelist = async (email) => {
  try {
    const whitelistRef = doc(db, 'whitelist', email.toLowerCase());
    await updateDoc(whitelistRef, {
      active: false,
      removedAt: serverTimestamp()
    });
    console.log(`✅ Removed ${email} from whitelist`);
    return true;
  } catch (error) {
    console.error('Error removing from whitelist:', error);
    return false;
  }
};

export const getWhitelistedUsers = async () => {
  try {
    const whitelistRef = collection(db, 'whitelist');
    const q = query(whitelistRef, where('active', '==', true));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  } catch (error) {
    console.error('Error getting whitelist:', error);
    return [];
  }
};

// Premium subscription management
export const upgradeToPremium = async (userId, subscriptionData = {}) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPremium: true,
      subscriptionStatus: 'active',
      subscriptionStartDate: serverTimestamp(),
      subscriptionEndDate: subscriptionData.endDate || null,
      stripeCustomerId: subscriptionData.customerId || null,
      stripeSubscriptionId: subscriptionData.subscriptionId || null,
      updatedAt: serverTimestamp()
    });
    console.log(`✅ Upgraded user ${userId} to Premium`);
    return true;
  } catch (error) {
    console.error('Error upgrading to premium:', error);
    return false;
  }
};

export const cancelPremium = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isPremium: false,
      subscriptionStatus: 'cancelled',
      subscriptionCancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    console.log(`✅ Cancelled premium for user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error cancelling premium:', error);
    return false;
  }
};

export const checkCanCreateGame = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    return { canCreate: false, reason: 'User not found' };
  }

  const userData = userDoc.data();

  // Check if user is premium
  if (userData.isPremium) {
    return { canCreate: true, reason: 'premium' };
  }

  // Check if user is whitelisted
  const isWhitelisted = await isUserWhitelisted(userData.email);
  if (isWhitelisted) {
    return { canCreate: true, reason: 'whitelisted' };
  }

  // Check free games remaining
  const freeGamesRemaining = await checkFreeGamesRemaining(userId);
  if (freeGamesRemaining > 0) {
    return { canCreate: true, reason: 'free', gamesRemaining: freeGamesRemaining };
  }

  return { canCreate: false, reason: 'limit_reached', gamesRemaining: 0 };
};

// ============================================
// PLAYER PRESENCE & DISCONNECT HANDLING
// ============================================
// DISABLED - Causing permission issues and false disconnects
// Leave Game buttons work without this

export const setupPlayerPresence = (gameId, userId, playerName) => {
  // Disabled - return no-op cleanup function
  return () => {};
};

export const subscribeToPlayerPresence = (gameId, callback) => {
  // Disabled - return no-op unsubscribe
  return () => {};
};

/**
 * Remove player from game (both graceful and abrupt exits)
 */
export const removePlayer = async (gameId, userId, reason = 'left') => {
  try {
    const gameRef = doc(db, 'games', gameId);
    const gameDoc = await getDoc(gameRef);

    if (!gameDoc.exists()) {
      throw new Error('Game not found');
    }

    const game = gameDoc.data();
    const player = game.players?.[userId];

    if (!player) {
      console.warn(`Player ${userId} not found in game ${gameId}`);
      return;
    }

    console.log(`🚪 Removing player ${player.name} from game ${gameId}. Reason: ${reason}`);

    // Remove from players object
    const updates = {
      [`players.${userId}`]: deleteField(),
      updatedAt: serverTimestamp()
    };

    // Remove from team array if assigned
    if (player.team) {
      const team = game.teams[player.team] || [];
      const updatedTeam = team.filter(id => id !== userId);
      updates[`teams.${player.team}`] = updatedTeam;
    }

    await updateDoc(gameRef, updates);

    // Clean up presence in Firestore
    const presenceRef = doc(db, 'games', gameId, 'presence', userId);
    await setDoc(presenceRef, {
      userId,
      name: player.name,
      online: false,
      leftAt: serverTimestamp()
    }).catch(err => console.warn('Could not update presence:', err));

    // Check if game should end due to insufficient players
    // Requirements: minimum 4 total players AND at least 2 players per team
    const remainingPlayers = Object.keys(game.players).filter(id => id !== userId);
    console.log(`👥 Remaining players: ${remainingPlayers.length}`);

    // Calculate remaining players per team (only if teams are assigned)
    let teamACount = 0;
    let teamBCount = 0;
    if (game.teamsAssigned && game.teams) {
      const teamA = (game.teams.teamA || []).filter(id => id !== userId);
      const teamB = (game.teams.teamB || []).filter(id => id !== userId);
      teamACount = teamA.length;
      teamBCount = teamB.length;
      console.log(`👥 Team counts - Team A: ${teamACount}, Team B: ${teamBCount}`);
    }

    // Check minimum requirements: 4 total players AND 2 per team (if teams assigned)
    const insufficientTotal = remainingPlayers.length < 4;
    const insufficientTeam = game.teamsAssigned && (teamACount < 2 || teamBCount < 2);

    if (insufficientTotal || insufficientTeam) {
      const reason = insufficientTeam
        ? `Team has fewer than 2 players (Team A: ${teamACount}, Team B: ${teamBCount})`
        : `Total players below minimum (${remainingPlayers.length}/4)`;
      console.log(`⚠️ ${reason} - ending game`);
      await updateDoc(gameRef, {
        status: 'finished',
        endReason: 'insufficient_players',
        endMessage: 'Game ended early - not enough players to continue (minimum 4 total and at least 2 players per team)',
        endedAt: serverTimestamp()
      });
      return {
        removed: true,
        playerName: player.name,
        wasPerformer: game.currentRoundData?.performerId === userId,
        gameEnded: true,
        reason: 'insufficient_players',
        message: 'Game ended early - not enough players to continue (minimum 4 total and at least 2 players per team)'
      };
    }

    console.log(`✅ Player ${player.name} removed successfully`);
    return { removed: true, playerName: player.name, wasPerformer: game.currentRoundData?.performerId === userId };
  } catch (error) {
    console.error('Error removing player:', error);
    throw error;
  }
};

/**
 * Migrate host to another player
 */
export const migrateHost = async (gameId, currentHostId, selectedNewHostId = null) => {
  try {
    const gameRef = doc(db, 'games', gameId);
    const gameDoc = await getDoc(gameRef);

    if (!gameDoc.exists()) {
      throw new Error('Game not found');
    }

    const game = gameDoc.data();

    // If new host explicitly selected, use that
    if (selectedNewHostId && game.players?.[selectedNewHostId]) {
      await updateDoc(gameRef, {
        hostId: selectedNewHostId,
        previousHostId: currentHostId,
        hostMigratedAt: serverTimestamp()
      });

      console.log(`✅ Host migrated to selected player: ${game.players[selectedNewHostId].name}`);
      return { newHostId: selectedNewHostId, newHostName: game.players[selectedNewHostId].name };
    }

    // Auto-select new host based on priority
    const players = Object.entries(game.players || {})
      .filter(([id]) => id !== currentHostId)
      .sort((a, b) => {
        // Sort by joinedAt timestamp (oldest first)
        const timeA = a[1].joinedAt?.seconds || 0;
        const timeB = b[1].joinedAt?.seconds || 0;
        return timeA - timeB;
      });

    if (players.length === 0) {
      // Last player - end game
      await updateDoc(gameRef, {
        status: 'finished',
        endReason: 'all_players_left',
        updatedAt: serverTimestamp()
      });
      console.log('⚠️ Last player left - game ended');
      return { gameEnded: true };
    }

    // Priority 1: Oldest player from same team as host
    const hostTeam = game.players[currentHostId]?.team;
    let newHost = null;

    if (hostTeam) {
      newHost = players.find(([id, p]) => p.team === hostTeam);
    }

    // Priority 2: Any oldest player (already sorted)
    if (!newHost) {
      newHost = players[0];
    }

    const [newHostId, newHostData] = newHost;

    await updateDoc(gameRef, {
      hostId: newHostId,
      previousHostId: currentHostId,
      hostMigratedAt: serverTimestamp()
    });

    console.log(`✅ Host auto-migrated to: ${newHostData.name}`);
    return { newHostId, newHostName: newHostData.name };
  } catch (error) {
    console.error('Error migrating host:', error);
    throw error;
  }
};
